
/**
 * Merge Azure DevOps Work Items into existing XTable rows/columns
 * @param {Array} azureWorkItems - Work items fetched from Azure Board
 * @returns {Object} { mergedRows, mergedColumns }
 */
import { v4 as uuidv4 } from "uuid";
import { findMaxColNum } from "../../../MiniProducts/xTables/helper/callbackHelper";

interface Column {
  colId: any;
  block: any;
  field: string;
  id: string;
  name: string;
  type: string;
}

interface Row {
  id: string;
  parentId?: string;   // for hierarchy
  data: Record<string, any>; // flexible data mapping columnId → value
  isAzureRow: boolean;
  l_id
}

function formatToMMDDYYYY(dateString: string): string {
  if (!dateString) return "";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return ""; // invalid date safeguard

  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
}
function stripHtmlTags(html: string): string {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, ""); // removes all HTML tags
}

function getAzureFieldValue(item: any, column: any) {
  const fields = item.fields;
  const referenceName = column?.referenceName ? column.referenceName : column.azureColumn
  // Case 1: Auto-generated grid column
  if (column.colId === "ag-Grid-AutoColumn") {
    return fields["System.Title"] ?? "";
  }
  if (column.referenceName === "System.Description")
    return stripHtmlTags(fields["System.Description"]);

  // Case 2: Assignee (special object handling)
  if (column.type === "Assignee") {
    const displayName = fields["System.AssignedTo"]?.displayName || "";
    const email = fields["System.AssignedTo"]?.uniqueName || "";
    const initials = displayName
    ?.split(" ")
    .filter(Boolean)
    .map((part) => part[0].toUpperCase())
    .join("");
    return displayName && displayName != '' && email && email != '' && initials && initials != '' ? JSON.stringify([{ username: displayName, email, initials }]) : '';
  }
  if (column.type === "person") {
    const assignee = fields[referenceName];
    if (!assignee) return "";
    const displayName = assignee.displayName || "";
    const email = assignee.uniqueName || "";
    const initials = displayName
      ?.split(" ")
      .filter(Boolean)
      .map((part) => part[0].toUpperCase())
      .join("");
    return displayName && displayName != '' && email && email != '' && initials && initials != '' ? JSON.stringify([{ username: displayName, email, initials }]) : '';
  }

  // Case 3: Status (wrap in rollup JSON)
  if (column.type === "shareableRollupList") {

    return fields[referenceName] ? JSON.stringify({ rowId: "", value: fields[referenceName] ?? "" }) : '';
  }

  // Case 4: For simple fields (reason, priority, date, tags, etc.)
  if (referenceName) {
    return column.type === 'date' ? formatToMMDDYYYY(fields[referenceName]) : fields[referenceName] ?? "";
  }

  // Fallback
  return "";
}


export function updateOrCreateObject(obj, field, newValue) {
  if (obj && typeof obj === "object") {
    // If the object exists, update the field with the new value
    obj[field] = newValue;
  } else {
    // If the object doesn't exist, create a new object with the field and new value
    obj = { [field]: newValue };
  }
  return obj;
}



function buildParentChildMap(workItems: any[]) {
  const parentMap: Record<string, string> = {}; // childId → parentId
  const childMap: Record<string, string[]> = {}; // parentId → [childIds]

  workItems.forEach((wi) => {
    wi.relations?.forEach((rel: any) => {
      const relatedId = extractWorkItemId(rel.url);

      if (rel.rel === "System.LinkTypes.Hierarchy-Forward") {
        // this wi is parent → relatedId is child
        if (!childMap[wi.id]) childMap[wi.id] = [];
        childMap[wi.id].push(relatedId);
        parentMap[relatedId] = wi.id;
      }

      if (rel.rel === "System.LinkTypes.Hierarchy-Reverse") {
        // this wi is child → relatedId is parent
        parentMap[wi.id] = relatedId;
        if (!childMap[relatedId]) childMap[relatedId] = [];
        childMap[relatedId].push(wi.id);
      }
    });
  });

  return { parentMap, childMap };
}

function extractWorkItemId(url: string) {
  return url.substring(url.lastIndexOf("/") + 1);
}
function buildLid(azureId: string, parentMap: Record<string, string>, azureToRowMap: Record<string, string>, cache: Record<string, string> = {}): string {
  if (cache[azureId]) return cache[azureId];

  const parentAzureId = parentMap[azureId];
  const myRowId = azureToRowMap[azureId];

  if (!parentAzureId) {
    cache[azureId] = myRowId; // root → just its rowId
  } else {
    const parentLid = buildLid(parentAzureId, parentMap, azureToRowMap, cache);
    cache[azureId] = parentLid + "." + myRowId;
  }

  return cache[azureId];
}
function insertMultipleRows({
  firstRowid,
  content,
  parentMap,
  workItem_id,
  azureToRowMap,
  insertRow,
}: {

  firstRowid?: string;
  content: any;
  insertRow?: any;
  parentMap?: any;
  azureToRowMap: any;
  workItem_id: any;
}) {
  let updatedRowData;
  let rowLid = buildLid(workItem_id, parentMap, azureToRowMap, {})
  // First row insert
  insertRow.insertRow({ l_id: rowLid }, (content) => {
    const firstBody = {
      ...content,
      id: firstRowid,
      l_id: rowLid,
    };
    updatedRowData = firstBody;

  });
  return { ...updatedRowData, ...content }
}

export function mergeAzureBoard(azureWorkItems, existingColumns, insertRow, azureFieldColumns) {

  // let azureColumns: Column[] = generateColumnsFromWorkItems(azureWorkItems, existingColumns);
  const maxNum = findMaxColNum(existingColumns);
  const azureColumns = azureFieldColumns?.filter(col => col.referenceName !== "System.Title" && col.referenceName !== "System.Id").map((col, index) => ({
    ...col,
    type: col.type === "dateTime" ? 'date' : col.referenceName === "System.AssignedTo" ? 'Assignee' : col.referenceName === "Microsoft.VSTS.Common.ClosedBy" || col.referenceName === "System.CreatedBy" || col.referenceName === "Microsoft.VSTS.Common.ResolvedBy" || col.referenceName === "Microsoft.VSTS.Common.ReviewedBy" ? 'person' : col.type === "double" || col.type === "integer" ? "number" : col.referenceName === "Microsoft.VSTS.Common.Severity" || col.referenceName === "System.State" ? "shareableRollupList" : col.referenceName === "System.WorkItemType" ? "dropdownsingle" : "text",
    field: `Column${maxNum + index + 1}`, // add the field here
  }));
  const mergedColumns = [...existingColumns, ...azureColumns];
  // let mergedColumns: Column[] = generateColumnsFromWorkItems(azureWorkItems, existingColumns);
  let mergedRows: Row[] = [];

  let workItems = azureWorkItems
  const azureToRowMap: Record<string, string> = {};

  workItems.forEach(wi => {
    const rowId = uuidv4();
    azureToRowMap[wi.id] = rowId;
  });
  // Step 2: Map Azure work items to XTable row format
  const { parentMap, childMap } = buildParentChildMap(workItems)
  // 2. Convert Azure workitems to rows with parent linkage;
  const azureRows = workItems.map((item) => {
    let data: any = {};

    mergedColumns.forEach((column) => {
      if (!column.block && column.colId !== '#' && column.referenceName !== "System.Id") {
        let cellValue = getAzureFieldValue(item, column);
        data[column.field] = cellValue
        data[column.field + '_es'] = updateOrCreateObject(data[column.field + '_es'], "value", cellValue)
      }
    });
    let firstRowid = azureToRowMap[item.id]
    let workItem_id = item.id

    data.isAzureRow = true
    let updatedRowData = insertMultipleRows({
      firstRowid,
      content: data,
      parentMap,
      workItem_id,
      azureToRowMap,
      insertRow,

    });
    return updatedRowData;
  });
  // Step 3: Merge rows (avoid duplicates)
  azureRows.forEach(row => {
    // if (!mergedRows.find(r => r.azure_id === row.azure_id)) {
    mergedRows.push(row);
    // }
  });
  // Step 3: Remove columns with no values across all rows
  const nonEmptyAzureColumns: any[] = [];
  const filteredOutAzureColumns: any[] = [];

  azureColumns.forEach((col: any) => {
    const hasValue = mergedRows.some(
      (row) =>
        row[col.field] !== null &&
        row[col.field] !== undefined &&
        row[col.field] !== ""
    );

    if (hasValue) {
      nonEmptyAzureColumns.push(col);
    } else {
      filteredOutAzureColumns.push(col);
    }
  });
  // 2. Delete filtered-out columns from all rows
  mergedRows.forEach((row) => {
    filteredOutAzureColumns.forEach((col) => {
      delete row[col.field]; // delete main field
      delete row[col.field + "_es"]// delete value4 if exists
    });
  });

  //  Step 5: Order rows so parents come before their children
  const rowMap = new Map<string, any>();
  mergedRows.forEach(row => rowMap.set(row.l_id, row));

  const getParentId = (id: string) => {
    return id.includes('.') ? id.split('.')[0] : null;
  };

  const childrenMap = new Map<string, any[]>();
  mergedRows.forEach(row => {
    const parentId = getParentId(row.l_id);
    if (parentId) {
      if (!childrenMap.has(parentId)) childrenMap.set(parentId, []);
      childrenMap.get(parentId)!.push(row);
    }
  });

  const orderedRows: any[] = [];
  const visited = new Set<string>();

  const addRowWithChildren = (row: any) => {
    if (visited.has(row.l_id)) return;
    visited.add(row.l_id);
    orderedRows.push(row);

    const children = childrenMap.get(row.l_id) || [];
    children.forEach(child => addRowWithChildren(child));
  };

  // First add parent rows (those without '.' in l_id)
  mergedRows
    .filter(row => !row.l_id.includes('.'))
    .forEach(parent => addRowWithChildren(parent));

  // Then add independent rows not already added
  mergedRows.forEach(row => {
    if (!visited.has(row.l_id)) orderedRows.push(row);
  });

  return { mergedRows:orderedRows, mergedColumns: nonEmptyAzureColumns };
}
